/**
 * The inline markdown the gap-enrichment agents write into their prose:
 * `**bold**` and `[label](url)`, and nothing else. The pipeline emits no
 * block-level markdown, so there are no headings, lists or code spans to parse
 * — anything that is not one of those two tokens passes through as text.
 *
 * The parse lives here rather than in a component because two surfaces render
 * the same strings and disagree about what a link IS: on the customer-facing
 * deal-intelligence page a `seenbefore:` link opens the past-project modal,
 * and in the internal gap-review reader it is only a label. One tokenizer, two
 * renderers — a second regex would drift the day the pipeline adds a token.
 */

export type InlineMarkdownToken =
  | { type: 'text'; text: string }
  | { type: 'bold'; children: InlineMarkdownToken[] }
  | { type: 'link'; label: string; url: string };

/**
 * The bold body excludes `*` so an unmatched asterisk cannot swallow the rest
 * of a paragraph, and the link target excludes whitespace so a `](` inside
 * ordinary prose is left alone. Both are what keep this safe to run over text
 * that was never meant as markdown.
 */
const INLINE_TOKEN_PATTERN = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

export function parseInlineMarkdown(text: string): InlineMarkdownToken[] {
  const tokens: InlineMarkdownToken[] = [];
  let cursor = 0;

  for (const tokenMatch of text.matchAll(INLINE_TOKEN_PATTERN)) {
    const [fullToken, boldText, linkLabel, linkUrl] = tokenMatch;

    if (tokenMatch.index > cursor) {
      tokens.push({ type: 'text', text: text.slice(cursor, tokenMatch.index) });
    }

    if (boldText !== undefined) {
      // Recurse: the enrichment wraps links in bold
      // (`**[MC1325414](https://…)**`), so a bold body is itself markdown.
      tokens.push({ type: 'bold', children: parseInlineMarkdown(boldText) });
    } else {
      tokens.push({ type: 'link', label: linkLabel ?? '', url: linkUrl ?? '' });
    }

    cursor = tokenMatch.index + fullToken.length;
  }

  if (cursor < text.length) {
    tokens.push({ type: 'text', text: text.slice(cursor) });
  }

  return tokens;
}
